import React, { useState } from "react";
import { toast } from "react-toastify";
import axios from "axios";
import { fetchTransactions } from "../../../RTK/transaction/GetTransaction";
import {
  Copy,
  X,
  ZoomIn,
  Download,
  CheckCircle2,
  XCircle,
  Loader2,
  Lock,
} from "lucide-react";
import { useDispatch } from "react-redux";
import PaymentConfirmationModal from "./PaymentConfirmationModal";

// Status Update Button Component
const StatusButton = ({
  status,
  currentStatus,
  onClick,
  isLoading,
  children,
  disabled = false,
}) => (
  <button
    onClick={onClick}
    disabled={currentStatus === status || isLoading}
    className={`
      px-4 py-2 rounded-lg transition-all flex items-center gap-2
      ${
        isLoading
          ? "bg-gray-700 text-gray-400 cursor-wait"
          : currentStatus === status
          ? "bg-gray-700 text-gray-400 cursor-not-allowed"
          : status === "paid"
          ? "bg-green-500/20 text-green-500 hover:bg-green-500/30"
          : "bg-red-500/20 text-red-500 hover:bg-red-500/30"
      }
    `}
  >
    {isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : children}
  </button>
);

// Image Preview Modal Component
const ImagePreviewModal = ({ image, onClose }) => (
  <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[60] flex items-center justify-center">
    <div className="relative max-w-4xl mx-auto p-4">
      <button
        onClick={onClose}
        className="absolute top-6 right-6 text-white p-2 rounded-full bg-black/50 hover:bg-black/70 transition-colors"
      >
        <X className="w-6 h-6" />
      </button>

      <img
        src={image}
        alt="Payment Preview"
        className="rounded-lg max-h-[80vh] w-auto object-contain"
      />

      <a
        href={image}
        download="payment-proof.jpg"
        className="absolute bottom-6 right-6 flex items-center gap-2 px-4 py-2 bg-white/10 hover:bg-white/20 text-white rounded-lg transition-colors"
      >
        <Download className="w-5 h-5" />
        تحميل الصورة
      </a>
    </div>
  </div>
);

// Info Item Component
const InfoItem = ({ label, value, copyable }) => (
  <div className="bg-gray-800 p-4 rounded-lg">
    <div className="text-gray-400 text-sm mb-1">{label}</div>
    <div className="text-white font-medium flex items-center justify-between">
      {value}
      {copyable && (
        <button
          onClick={() => {
            navigator.clipboard.writeText(value);
            toast.success("تم النسخ بنجاح");
          }}
          className="p-1 hover:text-yellow-500 transition-colors"
        >
          <Copy className="w-4 h-4" />
        </button>
      )}
    </div>
  </div>
);

// Main Modal Component
const TransactionDetailsModal = ({
  isOpen,
  onClose,
  transactionData,
  onStatusUpdate,
  canUpdateStatus,
}) => {
  const [isImagePreviewOpen, setIsImagePreviewOpen] = useState(false);
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);
  // const dispatch = useDispatch();
  // In the TransactionDetailsModal component, add new state:
  const [isPaymentConfirmationOpen, setIsPaymentConfirmationOpen] =
    useState(false);

  if (!isOpen || !transactionData) return null;

  const { transaction } = transactionData;

  const handleStatusUpdate = async (newStatus, additionalData = {}) => {

    if (!canUpdateStatus) {
      toast.error("ليس لديك صلاحية لتحديث حالة المعاملات 🔒");
      return;
    }

    try {
      setIsUpdatingStatus(true);
      const roundedAmount = parseFloat(transaction.amount).toFixed(2);

      const response = await axios.patch(
        `${process.env.REACT_APP_API_URL}/GetTransaction/${transaction.id}/UpdateStatus/`,
        {
          status: newStatus,
          amount: roundedAmount,
          ...additionalData,
        },
        {
          headers: {
            accept: "application/json",
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
          },
        }
      );

      if (response.data) {
        toast.success(
          newStatus === "paid"
            ? "تم تحديث الحالة إلى مدفوعة"
            : "تم تحديث الحالة إلى فاشلة"
        );
        if (onStatusUpdate) {
          onStatusUpdate(response.data);
        }
        onClose();
      }
    } catch (error) {
      console.error("Error updating status:", error);
      toast.error(error.response?.data?.message || "فشل في تحديث الحالة");
    } finally {
      setIsUpdatingStatus(false);
    }
  };

  //=========================================================

  // Update the StatusButton component for "paid" status:
  <StatusButton
    status="paid"
    currentStatus={transaction.status}
    onClick={() => setIsPaymentConfirmationOpen(true)}
    isLoading={isUpdatingStatus}
  >
    <CheckCircle2 className="w-4 h-4" />
    مدفوعة
  </StatusButton>;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50"
        onClick={onClose}
      />
      {/* Modal */}
      <div
        className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full max-w-2xl bg-gray-900 rounded-xl shadow-2xl z-50 border border-gray-800"
        dir="rtl"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-800">
          <div className="flex items-center gap-4">
            <div
              className="w-12 h-12 rounded-full overflow-hidden bg-gray-800"
              dangerouslySetInnerHTML={{ __html: transaction.user.svg_image }}
            />
            <div>
              <h3 className="text-xl font-bold text-white">
                {transaction.user.username}
              </h3>
              <p className="text-gray-400">
                رصيد الحساب: ${transaction.user.balance}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white p-2 rounded-lg hover:bg-gray-800 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Transaction Status */}
          <div className="flex justify-between items-center bg-gray-800 p-4 rounded-lg">
            <span className="text-gray-400">حالة المعاملة</span>
            <span
              className={`px-3 py-1 rounded-full text-sm font-medium ${
                transaction.status === "paid"
                  ? "bg-green-500/10 text-green-500"
                  : transaction.status === "failed"
                  ? "bg-red-500/10 text-red-500"
                  : "bg-yellow-500/10 text-yellow-500"
              }`}
            >
              {transaction.status === "paid"
                ? "مكتملة"
                : transaction.status === "failed"
                ? "فاشلة"
                : "معلقة"}
            </span>
          </div>

          {/* Transaction Details */}
          <div className="grid grid-cols-2 gap-4">
            <InfoItem
              label="رقم المعاملة"
              value={transaction.transaction_id}
              copyable
            />
            <InfoItem
              label="المبلغ"
              value={`$${Number(transaction.amount).toFixed(2)}`}
            />
            <InfoItem label="العمولة" value={`$${transaction.commission}`} />
            <InfoItem
              label="طريقة الدفع"
              value={transaction.payment_method.name}
            />
            <InfoItem
              label="نوع المعاملة"
              value={transaction.trnsaction_type}
            />
            <InfoItem
              label="تاريخ المعاملة"
              value={new Date(transaction.created_at).toLocaleString("ar-EG")}
            />
          </div>

          {/* Payment Image */}
          {transaction.image && (
            <div className="space-y-2">
              <h4 className="text-lg font-semibold text-white">صورة الدفع</h4>
              <div
                className="relative group cursor-pointer"
                onClick={() => setIsImagePreviewOpen(true)}
              >
                <div className="rounded-lg overflow-hidden w-fit">
                  <img
                    src={transaction.image}
                    alt="Payment Proof"
                    className="w-20 h-20 object-cover rounded-lg group-hover:opacity-90 transition-opacity"
                  />
                </div>
                <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                  <div className="bg-black/50 p-2 rounded-full">
                    <ZoomIn className="w-5 h-5 text-white" />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Additional Details */}
          {transaction.transaction_details && (
            <div className="space-y-2">
              <h4 className="text-lg font-semibold text-white">
                تفاصيل إضافية
              </h4>
              <pre className="bg-gray-800 p-4 rounded-lg overflow-auto text-gray-300">
                {JSON.stringify(transaction.transaction_details, null, 2)}
              </pre>
            </div>
          )}
        </div>
        
        {/* Status Update Section */}
      {/* <div className="bg-gray-800 p-4 rounded-lg">
        <div className="flex items-center justify-between mb-3">
          <h4 className="text-sm font-medium text-gray-400">تحديث الحالة</h4>
          {!canUpdateStatus && (
            <div className="flex items-center gap-2 text-red-400 text-xs">
              <Lock className="w-3 h-3" />
              <span>ليس لديك صلاحية التحديث</span>
            </div>
          )}
        </div>
      </div> */}


        {/* Footer */}
        <div className="flex justify-between gap-3 p-6 border-t border-gray-800">
          {/* Left Side: Status Buttons or Permission Message */}
          {canUpdateStatus !== false ? (
            <div className="flex items-center gap-2">
              <StatusButton
                status="paid"
                currentStatus={transaction.status}
                onClick={() => setIsPaymentConfirmationOpen(true)}
                isLoading={isUpdatingStatus}
              >
                <CheckCircle2 className="w-4 h-4" />
                مدفوعة
              </StatusButton>

              <StatusButton
                status="failed"
                currentStatus={transaction.status}
                onClick={() => handleStatusUpdate("failed")}
                isLoading={isUpdatingStatus}
              >
                <XCircle className="w-4 h-4" />
                فاشلة
              </StatusButton>
            </div>
          ) : (
            <div className="flex items-center gap-2 px-4 py-2 bg-slate-800 border border-slate-700 rounded-lg text-slate-400">
              <Lock className="w-5 h-5" />
              <span className="text-sm">ليس لديك صلاحية لتحديث المعاملات</span>
            </div>
          )}

          {/* Right Side: Close and Invoice Buttons */}
          <div className="flex items-center gap-2">
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-400 hover:text-white hover:bg-gray-800 rounded-lg transition-colors"
            >
              إغلاق
            </button>
            {transaction.invoice_id && (
              <button
                onClick={() => window.open(transaction.checkout_url, "_blank")}
                className="px-4 py-2 bg-yellow-500 text-black rounded-lg hover:bg-yellow-600 transition-colors"
              >
                عرض الفاتورة
              </button>
            )}
          </div>
        </div>
      </div>
      {/* Image Preview Modal */}
      {isImagePreviewOpen && (
        <ImagePreviewModal
          image={transaction.image}
          onClose={() => setIsImagePreviewOpen(false)}
        />
      )}
      {/* Add the PaymentConfirmationModal */}
      <PaymentConfirmationModal
        isOpen={isPaymentConfirmationOpen}
        onClose={() => setIsPaymentConfirmationOpen(false)}
        transaction={transaction}
        onConfirm={(data) => handleStatusUpdate("paid", data)}
      />
    </>
  );
};

export default TransactionDetailsModal;
